/**
 * The one mutable thing in the game: a serializable {@link GameState} (tech-design
 * §2.1). Everything derived — rates, costs, unlock visibility — is computed by pure
 * selectors and never stored here, which keeps saves small and migrations rare.
 *
 * The engine is pure and headless (no DOM, Pixi, IndexedDB, or wall-clock time).
 * Wall-clock stamps (`createdAt`, `lastSavedAt`) live in the persistence envelope,
 * not in state — `meta.ticks` is the engine's own deterministic clock.
 */
import { ONE, ZERO, type Num } from './num.js';
import { createRng, type RngState } from './rng.js';

/** Bumped when the serialized shape changes; drives save migrations (persistence). */
export const SAVE_FORMAT_VERSION = 3;

export interface ResourceState {
  /** Current on-hand amount. */
  readonly amount: Num;
  /** Total ever produced — the metric prestige gain and lifetime unlocks read. */
  readonly lifetime: Num;
}

export interface GeneratorState {
  /** Owned count — a small integer, so a raw `number` is fine (§2.6). */
  readonly owned: number;
  /** Cached production multiplier from stacked effects (recomputed on change). */
  readonly multiplier: Num;
}

export interface BreakthroughState {
  readonly attempts: number;
  /** Accumulated pity — added to base chance, reset on hit if configured (§7). */
  readonly pity: number;
  readonly hits: number;
}

export interface PrestigeLayerState {
  readonly currency: Num;
  readonly purchased: readonly string[];
  readonly count: number;
}

export interface AgesState {
  /** The age currently being played, or `null` before any age is selected. */
  readonly current: string | null;
  readonly completed: readonly string[];
}

export interface Meta {
  readonly version: number;
  /** Logic ticks elapsed since a fresh start — the deterministic in-engine clock. */
  readonly ticks: number;
  /** Highest age `order` ever played, across every reset — a lifetime stat that
   * gates full-reset prestige layers (GAM-11 `unlockAgeOrder`). */
  readonly maxAgeOrderReached: number;
}

export interface GameState {
  readonly resources: Readonly<Record<string, ResourceState>>;
  readonly generators: Readonly<Record<string, GeneratorState>>;
  readonly upgrades: readonly string[];
  readonly traits: readonly string[];
  readonly milestones: readonly string[];
  readonly breakthroughs: Readonly<Record<string, BreakthroughState>>;
  readonly ages: AgesState;
  readonly prestige: Readonly<Record<string, PrestigeLayerState>>;
  readonly rng: RngState;
  readonly meta: Meta;
}

const DEFAULT_SEED = 'incrementus';

export interface InitOptions {
  readonly seed?: string;
  readonly currentAge?: string;
}

/**
 * A fresh game. Resource/generator entries are created lazily (a missing entry is
 * read as zero), so a fresh start needs no content and stays tiny.
 */
export function createInitialState(options: InitOptions = {}): GameState {
  return {
    resources: {},
    generators: {},
    upgrades: [],
    traits: [],
    milestones: [],
    breakthroughs: {},
    ages: { current: options.currentAge ?? null, completed: [] },
    prestige: {},
    rng: createRng(options.seed ?? DEFAULT_SEED),
    meta: { version: SAVE_FORMAT_VERSION, ticks: 0, maxAgeOrderReached: 0 },
  };
}

// ── Lazy accessors — a missing entry reads as its zero value ──────────────────

const ZERO_RESOURCE: ResourceState = { amount: ZERO, lifetime: ZERO };

export function getResource(state: GameState, id: string): ResourceState {
  return state.resources[id] ?? ZERO_RESOURCE;
}

export function getGenerator(state: GameState, id: string): GeneratorState {
  return state.generators[id] ?? { owned: 0, multiplier: ONE };
}

export function getBreakthrough(state: GameState, id: string): BreakthroughState {
  return state.breakthroughs[id] ?? { attempts: 0, pity: 0, hits: 0 };
}

export function getPrestige(state: GameState, id: string): PrestigeLayerState {
  return state.prestige[id] ?? { currency: ZERO, purchased: [], count: 0 };
}

export const hasUpgrade = (state: GameState, id: string): boolean =>
  state.upgrades.includes(id);
export const hasTrait = (state: GameState, id: string): boolean =>
  state.traits.includes(id);
export const hasMilestone = (state: GameState, id: string): boolean =>
  state.milestones.includes(id);
export const isAgeCompleted = (state: GameState, id: string): boolean =>
  state.ages.completed.includes(id);

// ── Immutable updates — return a new state, never mutate ──────────────────────

/** Add an id to a set-like collection, de-duplicating. */
export function addToSet(set: readonly string[], id: string): readonly string[] {
  return set.includes(id) ? set : [...set, id];
}
